import React, { useState } from 'react';
import { CheckCircle2, Bot, Grid3x3, Columns } from 'lucide-react';
import MessageRenderer from './MessageRenderer';

const BurstResponse = ({ message, allModels, onSelectResponse, streamingResponses = {} }) => {
  const [selectedModelId, setSelectedModelId] = useState(message.selectedModel || null);
  const [viewMode, setViewMode] = useState('side-by-side'); // 'side-by-side' or 'grid'
  const responses = message.burstResponses || {};
  const isStreaming = message.isStreaming || false;
  
  // Merge streaming responses with saved responses
  const allResponses = { ...responses };
  Object.entries(streamingResponses).forEach(([key, value]) => {
    if (key.startsWith(message.userMessageId)) {
      const modelId = key.split('-').slice(1).join('-');
      allResponses[modelId] = value;
    }
  });
  
  const handleSelectResponse = (modelId) => {
    setSelectedModelId(modelId);
    if (onSelectResponse) {
      onSelectResponse(message.id, modelId);
    }
  };
  
  const getModelName = (modelId) => {
    for (const [provider, models] of Object.entries(allModels)) {
      // Ensure models is an array before calling find
      if (Array.isArray(models)) {
        const model = models.find(m => m.id === modelId);
        if (model) return model.name;
      }
    }
    return modelId;
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 font-medium">
          Burst Mode Responses ({Object.keys(responses).length} models)
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('side-by-side')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
              viewMode === 'side-by-side' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Side by side view"
          >
            <Columns className="w-4 h-4" />
            Side by Side
          </button>
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
              viewMode === 'grid' 
                ? 'bg-purple-100 text-purple-700' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            title="Grid view"
          >
            <Grid3x3 className="w-4 h-4" />
            Grid
          </button>
        </div>
      </div>
      
      <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 gap-4" : "flex gap-4 overflow-x-auto pb-2"} style={viewMode === 'side-by-side' ? { scrollbarWidth: 'thin' } : {}}>
        {Object.entries(allResponses).map(([modelId, response]) => (
          <div 
            key={modelId}
            className={`border rounded-lg p-4 cursor-pointer transition-all ${
              selectedModelId === modelId 
                ? 'border-purple-500 shadow-lg bg-purple-50' 
                : 'border-gray-200 hover:border-gray-300 hover:shadow'
            } ${
              viewMode === 'side-by-side' ? 'flex-shrink-0 w-96' : ''
            }`}
            onClick={() => handleSelectResponse(modelId)}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Bot className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-sm">{getModelName(modelId)}</span>
              </div>
              {selectedModelId === modelId && (
                <CheckCircle2 className="w-5 h-5 text-purple-600" />
              )}
            </div>
            
            <div className={viewMode === 'side-by-side' ? "overflow-y-auto" : "overflow-y-auto"} style={{ maxHeight: viewMode === 'side-by-side' ? '500px' : '400px' }}>
              <MessageRenderer 
                content={response || '...'} 
                isStreaming={isStreaming && !response}
              />
            </div>
          </div>
        ))}
      </div>
      
      {selectedModelId && (
        <div className="text-sm text-purple-600 font-medium text-center">
          Selected: {getModelName(selectedModelId)}
        </div>
      )}
    </div>
  );
};

export default BurstResponse;